/**
 * Shows a list of users which are registered for the application.
 * Material design: Table from https://material-ui.com/components/tables/#table
 */

import React, {useContext, useEffect, useState} from 'react'
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import {NavLink} from "react-router-dom";
import AppbarActionButton from "../../../../commons/elements/buttons/AppbarActionButton";
import AddIcon from "@mui/icons-material/Add";
import {AppbarContentContext} from "../../../../commons/routing/appbar/AppbarContentProvider";
import {useStyles} from "./PlaylistsSection.css";
import {StyledTableCell, StyledTableRow} from "../../../../commons/utils/table/StyledTablePrimary.css";
import useListPlaylists from "./useListPlaylists";
import {Box, CircularProgress, Collapse, IconButton} from "@mui/material";
import {Delete, Edit, Info, KeyboardArrowDown, KeyboardArrowUp, VideoLibrary} from "@mui/icons-material";
import ScoredScenesInPlaylistSection from "../scoredScenesSummary/ScoredScenesInPlaylistSection";
import usePagination from "../../../../commons/utils/usePagination";
import LoadingProgressIndication from "../../../../commons/elements/progress/loading/LoadingProgressIndication";
import {useIntl} from "react-intl";
import AssociationLogo from "../../../../commons/utils/logo/AssociationLogo";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import FeedbackModeIndication from "../utils/FeedbackModeIndication";
import PlaylistStatusIndication from "../utils/PlaylistStatusIndication";
import GenericFilter from "../../../../commons/elements/search/genericSearch/GenericFilter";


const PlaylistsSection = () => {
    const classes = useStyles()
    const {setCurrentAppbarContent} = useContext(AppbarContentContext)

    // Pagination for the table handling
    const {page, rowsPerPage, handleChangePage, handleChangeRowsPerPage} = usePagination()

    // Hook providing the list user functionality
    const {playlists, loading, handleDeletePlaylist, deletionInProgress, deletionAttemptedPlaylist} = useListPlaylists()

    // State which holds the playlists according to the current filter
    const [filteredPlaylists, setFilteredPlaylists] = useState([])

    // Collapsible table state
    const [openIndex, setOpenIndex] = React.useState();

    // Internationalization for simple strings
    const intl = useIntl();

    // Checking if the edit/saving operations are allowed
    const {isOperationOnObjectAllowed} = useContext(FirebaseAuthContext);

    // Setting the appbar content
    useEffect(() => {
        setFilteredPlaylists(playlists)

        setCurrentAppbarContent({
            header: intl.formatMessage({id: 'app.playlists.overview.appbar.header', defaultMessage: 'Playlists'}),
            description: intl.formatMessage({
                id: 'app.playlists.overview.appbar.description',
                defaultMessage: 'Manage the playlists...'
            }),
            actions: <>
                <AppbarActionButton
                    label={intl.formatMessage({
                        id: 'app.playlists.overview.appbar.actions.new',
                        defaultMessage: 'New Playlist'
                    })}
                    component={NavLink}
                    to='/admin/playlists/new'
                    icon={<AddIcon/>}/>
            </>,
            searchContainer: <GenericFilter objects={playlists}
                                            onResultsChanged={(results) => setFilteredPlaylists(results)}
                                            filterDefinitions={[
                                                {
                                                    type: 'select',
                                                    id: 'app.playlists.filter.name',
                                                    options: [...new Set(playlists.map((p) => p.name))],
                                                    getOptionLabel: (p) => p,
                                                    condition: (object, filter) => filter?.includes(object.name) || filter?.length === 0
                                                },
                                                {
                                                    type: 'select',
                                                    id: 'app.playlists.filter.mode',
                                                    options: [...new Set(playlists.map((p) => p.feedbackMode))],
                                                    getOptionLabel: (feedbackMode) =>
                                                        intl.formatMessage({
                                                            id: `app.playlists.edit.form.feedbackMode.${feedbackMode}.label`,
                                                            defaultMessage: feedbackMode
                                                        }),
                                                    condition: (object, filter) => filter?.includes(object.feedbackMode) || filter?.length === 0
                                                },
                                                {
                                                    type: 'select',
                                                    id: 'app.playlists.filter.status',
                                                    options: [...new Set(playlists.map((p) => getPlaylistStatus(p)))],
                                                    getOptionLabel: (status) =>
                                                        intl.formatMessage({
                                                            id: `app.ids.playlistStatus.${status}`,
                                                            defaultMessage: status
                                                        }),
                                                    condition: (object, filter) => filter?.includes(getPlaylistStatus(object)) || filter?.length === 0
                                                }
                                            ]}/>,
            progress: deletionInProgress
        })
    }, [deletionInProgress, playlists])

    const getPlaylistStatus = (playlist) => {
        let pStatus = 'approved'

        for (let scoredScene of playlist.scoredScenes) {
            const status = scoredScene?.statusInfo?.status
            if (status === 'rejected') {
                pStatus = 'erroneous'
                break;
            }
            if (!status || status === 'draft') {
                pStatus = 'unapproved'
            }
        }

        return pStatus
    }

    // Show loading indication while loading the list of playlists
    if (loading) {
        return <LoadingProgressIndication
            text={intl.formatMessage({
                id: 'app.playlists.overview.loading',
                defaultMessage: 'Loading playlists...'
            })}/>
    }

    // Shows the table containing the playlists
    return (
        <Paper className={classes.root}>
            <TableContainer className={classes.container}>
                <Table stickyHeader aria-label="sticky table">

                    {/* Table header */}
                    <TableHead>
                        <TableRow>
                            <StyledTableCell key="collapsible"></StyledTableCell>
                            <StyledTableCell key='name'>
                                {intl.formatMessage({
                                    id: 'app.playlists.overview.table.column.name',
                                    defaultMessage: 'Name'
                                })}
                            </StyledTableCell>
                            <StyledTableCell key='numberScenes'><VideoLibrary/></StyledTableCell>
                            <StyledTableCell key='mode'>
                                {intl.formatMessage({
                                    id: 'app.playlists.overview.table.column.mode',
                                    defaultMessage: 'Mode'
                                })}
                            </StyledTableCell>
                            <StyledTableCell key='status'>
                                {intl.formatMessage({
                                    id: 'app.playlists.overview.table.column.status',
                                    defaultMessage: 'Status'
                                })}
                            </StyledTableCell>
                            <StyledTableCell align={"right"} key='actions'>
                                {intl.formatMessage({
                                    id: 'app.playlists.overview.table.column.actions',
                                    defaultMessage: 'Actions'
                                })}
                            </StyledTableCell>
                        </TableRow>
                    </TableHead>

                    {/* Table rows */}
                    <TableBody>
                        {filteredPlaylists.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((playlistRow, index) => {
                            return (
                                <React.Fragment key={playlistRow.key}>
                                    <StyledTableRow hover key={playlistRow.key}>
                                        <StyledTableCell key="collapsible">
                                            {openIndex === index
                                                ? <IconButton aria-label="expand row" size="small"
                                                              onClick={() => setOpenIndex(null)}>
                                                    <KeyboardArrowUp/>
                                                </IconButton>
                                                : <IconButton aria-label="expand row" size="small"
                                                              onClick={() => setOpenIndex(index)}>
                                                    <KeyboardArrowDown/>
                                                </IconButton>}
                                        </StyledTableCell>

                                        <StyledTableCell key='name'>
                                            <div className={classes.row}>
                                                <AssociationLogo association={playlistRow.association} height={45}
                                                                 width={45}/>
                                                <div className={classes.playlistName}>
                                                    {playlistRow.name}
                                                </div>
                                            </div>
                                        </StyledTableCell>

                                        <StyledTableCell key='numberScenes'>
                                            <div className={classes.numberOfScenes}>
                                                {playlistRow.scoredScenesKeys.length}
                                            </div>
                                        </StyledTableCell>

                                        <StyledTableCell key='mode'>
                                            <FeedbackModeIndication feedbackMode={playlistRow.feedbackMode}/>
                                        </StyledTableCell>

                                        <StyledTableCell key='status'>
                                            <PlaylistStatusIndication playlist={playlistRow}/>
                                        </StyledTableCell>

                                        <StyledTableCell align={"right"} key='actions'>
                                            <IconButton
                                                component={NavLink}
                                                to={'/admin/playlists/' + playlistRow.key + '/edit'}
                                                size="large">
                                                {isOperationOnObjectAllowed(playlistRow)
                                                    ? <Edit/>
                                                    : <Info/>}
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleDeletePlaylist(playlistRow)}
                                                disabled={!isOperationOnObjectAllowed(playlistRow)}
                                                size="large">
                                                {playlistRow.key === deletionAttemptedPlaylist
                                                    ? <CircularProgress style={{height: 20, width: 20}}/>
                                                    : <Delete/>}
                                            </IconButton>
                                        </StyledTableCell>
                                    </StyledTableRow>

                                    {/* Collapsible section */}
                                    <StyledTableRow>
                                        <StyledTableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={6}>
                                            <Collapse in={openIndex === index} timeout="auto" unmountOnExit>
                                                <Box margin={1}>
                                                    <ScoredScenesInPlaylistSection playlistKey={playlistRow.key}/>
                                                </Box>
                                            </Collapse>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination section */}
            <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={filteredPlaylists.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
    );
}

export default PlaylistsSection
